// Posting a remittance on Claims → Runs: a GAMMIS RA PDF (parsed by the
// parse-ra function), an 835 file chosen by hand, or an 835 the SFTP worker
// already fetched — all reviewed here and posted through the same rule.
//
// Moved out of pages/finance/BillingRun.js on 2026-10-20. Nothing is posted
// until "Post payments"; the writes are lib/billing/remittance.js.

import { useEffect, useRef, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { parse835 } from '../../../lib/edi/x835';
import { claimFromRaRow } from '../../../lib/edi/remitPosting';
import { postRemittance as postRemittanceToRun } from '../../../lib/billing/remittance';
import { RemittanceOutcome } from './RunPanels';
import { fmt$2 } from './format';

/**
 * @param {object}   p.initial        { runId, dateFrom, dateTo, storedId?, parsed? } — `parsed`
 *                                    opens a stored 835 straight at its review step
 * @param {object}   p.profile        the signed-in user, recorded on the receipt
 * @param {Function} p.onClose
 * @param {Function} p.onPosted       ({ runId, stored }) after payments are written
 * @param {Function} p.onStoredPosted reloads the waiting-remittance list, before results show
 */
export default function RemittanceModal({ initial, profile, onClose, onPosted, onStoredPosted }) {
  const eraFileRef                  = useRef(null);
  const [eraModal,   setEraModal]   = useState(() => ({
    runId: initial.runId, dateFrom: initial.dateFrom, dateTo: initial.dateTo,
    ...(initial.storedId ? { storedId: initial.storedId } : {}),
  }));
  const [eraStep,    setEraStep]    = useState(initial.parsed ? 'review' : 'pick');
  const [eraParsed,  setEraParsed]  = useState(initial.parsed || null);
  const [eraResults, setEraResults] = useState(null);
  const [eraError,   setEraError]   = useState('');

  // PDF RA upload
  const raFileRef                   = useRef(null);
  const raAbortRef                  = useRef(null);
  const raCancelledRef              = useRef(false);
  const [raStep,     setRaStep]     = useState('type');  // type | uploading | review | posting | done
  const [raParsed,   setRaParsed]   = useState(null);
  const [raResults,  setRaResults]  = useState(null);
  const [raError,    setRaError]    = useState('');
  const [raParsing,  setRaParsing]  = useState(false);
  const [raElapsed,  setRaElapsed]  = useState(0);

  useEffect(() => {
    if (!raParsing) { setRaElapsed(0); return; }
    const start = Date.now();
    const id = setInterval(() => setRaElapsed(Math.floor((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(id);
  }, [raParsing]);

  // ── PDF RA upload ─────────────────────────────────────────────────────────
  const handleRAPdfFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setRaError(''); setRaParsing(true); setRaStep('uploading');
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (ev) => resolve(ev.target.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const { data: { session } } = await supabase.auth.getSession();
      const authHeaders = {
        'content-type': 'application/json',
        'Authorization': `Bearer ${session?.access_token}`,
        'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
      };
      const base = process.env.REACT_APP_SUPABASE_URL;

      // Start Drive upload immediately (fire-and-forget) — don't block the user on it.
      // Drive URL will be attached to state when it resolves in the background.
      fetch(`${base}/functions/v1/upload-ra-to-drive`, {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({ pdf_base64: base64, filename: file.name }),
      }).then(r => r.json()).then(data => {
        if (data.webViewLink) setEraModal(prev => ({ ...prev, raPdfUrl: data.webViewLink }));
      }).catch(() => {}); // Drive failure is non-fatal

      // parse-ra streams SSE heartbeats while Claude generates, then sends the
      // final result as "data: {json}\n\n". We read the stream chunk-by-chunk
      // and close it ourselves as soon as the data line arrives — browsers treat
      // text/event-stream as a persistent connection and .text() never resolves.
      const raAbort = new AbortController();
      raAbortRef.current = raAbort;
      const raTimeout = setTimeout(() => raAbort.abort(), 240_000);
      let parsed;
      try {
        const response = await fetch(`${base}/functions/v1/parse-ra`, {
          method: 'POST', headers: authHeaders,
          body: JSON.stringify({ pdf_base64: base64 }),
          signal: raAbort.signal,
        });
        if (!response.ok) {
          let detail = `Server error ${response.status}`;
          try {
            const errBody = await response.json();
            if (errBody?.error) detail = errBody.error;
          } catch { /* body wasn't JSON — keep generic status message */ }
          throw new Error(detail);
        }
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let dataLine = null;
        while (!dataLine) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          // Only the fully-terminated lines are safe to inspect — the large
          // "data: {...}" payload routinely spans multiple network chunks,
          // and checking an incomplete trailing fragment truncates the JSON.
          const lines = buffer.split('\n');
          buffer = lines.pop();
          dataLine = lines.find(l => l.startsWith('data: ')) || null;
        }
        reader.cancel();
        parsed = JSON.parse(dataLine ? dataLine.slice(5).trim() : '{}');
      } finally {
        clearTimeout(raTimeout);
      }
      if (!parsed || parsed.error) throw new Error((parsed && parsed.error) || 'Parse failed');

      setRaParsed(parsed);
      setRaStep('review');
    } catch (err) {
      if (err.name === 'AbortError' && raCancelledRef.current) {
        raCancelledRef.current = false;
      } else {
        const msg = err.name === 'AbortError'
          ? 'Upload timed out (4 min). The PDF may be too large — try splitting it or retry.'
          : 'Could not process PDF: ' + err.message;
        setRaError(msg);
      }
      setRaStep('type');
    } finally {
      setRaParsing(false);
    }
  };

  const postRAClaims = async () => {
    if (!raParsed || !eraModal) return;
    setRaStep('posting');
    try {
      // The PDF and the 835 describe the same event differently; claimFromRaRow
      // brings this one into the shared shape so both post through the same
      // rules. This path used to carry its own matching, which fell back to
      // "any line for this client" with nothing stopping two claims landing on
      // one line, and put the whole EOB string into denial_code.
      const claims = (raParsed.claims || []).map(row =>
        claimFromRaRow(row, { checkDate: raParsed.check_date || null }));

      const results = await postRemittance(eraModal.runId, claims, {
        source: 'gammis_pdf',
        ra_number: raParsed.ra_number || null,
        payment_date: raParsed.check_date || null,
        payment_amount: raParsed.total_paid ?? null,
        payer_name: 'Georgia Medicaid',
        file_url: eraModal.raPdfUrl || null,
      });

      setRaResults(results);
      setRaStep('done');
      onPosted({ runId: eraModal.runId, stored: false });
    } catch (err) {
      setRaError('Failed to post payments: ' + err.message);
      setRaStep('review');
    }
  };

  // Apply a parsed remittance — lib/billing/remittance.js, the same for the
  // RA PDF, an uploaded 835 and one the SFTP worker already stored.
  const postRemittance = (billingRunId, claims, remittance, { existingId = null } = {}) =>
    postRemittanceToRun(supabase, { billingRunId, claims, remittance, profile, existingId, now: new Date().toISOString() });

  // ── ERA upload ────────────────────────────────────────────────────────────
  const handleERAFile = (e) => {
    const file = e.target.files?.[0]; if (!file) return; setEraError('');
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const result = parse835(ev.target.result);
        if (!result || !result.claims.length) { setEraError('No claim records found. Make sure this is a valid 835 ERA file.'); return; }
        setEraParsed(result); setEraStep('review');
      } catch (err) { setEraError('Could not parse ERA file: ' + err.message); }
    };
    reader.readAsText(file);
  };
  const postERA = async () => {
    if (!eraParsed || !eraModal) return;
    setEraStep('posting');
    try {
      const results = await postRemittance(eraModal.runId, eraParsed.claims, {
        source: '835',
        trace_number: eraParsed.checkNumber || null,
        check_number: eraParsed.checkNumber || null,
        payment_date: eraParsed.checkDate || null,
        payment_amount: eraParsed.totalPayment ?? null,
        payer_name: eraParsed.payer || null,
      }, { existingId: eraModal.storedId || null });
      if (eraModal.storedId) await onStoredPosted();
      setEraResults(results); setEraStep('done');
      onPosted({ runId: eraModal.runId, stored: !!eraModal.storedId });
    } catch (err) { setEraError('Failed to post payments: ' + err.message); setEraStep('review'); }
  };

  return (
      <div className="modal-overlay drawer" onMouseDown={e => e.target === e.currentTarget && onClose()}>
        <div className="modal" style={{ maxWidth: (eraStep === 'review' || raStep === 'review') ? 700 : 520 }} onMouseDown={e => e.stopPropagation()}>
          <div className="modal-header">
            <div>
              <div className="modal-title">
                {eraModal.storedId ? 'Remittance Received — Post Payments' : 'Upload Remittance Advice — Post Payments'}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{eraModal.dateFrom} – {eraModal.dateTo}</div>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={() => onClose()}>✕</button>
          </div>
          <div className="modal-body">

            {/* ── Type picker ── */}
            {eraStep === 'pick' && raStep === 'type' && (
              <>
                <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
                  Choose your remittance format. GAMMIS sends PDF remittance advice (CRA-xxxxx-R). An 835 collected over SFTP appears on the run itself and does not need uploading; use this only for a file you downloaded by hand.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div onClick={() => raFileRef.current?.click()}
                    style={{ border: '1.5px solid var(--border)', borderRadius: 10, padding: '20px 16px', textAlign: 'center', cursor: 'pointer', background: 'var(--bg2)' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>
                    <div style={{ fontSize: 28, marginBottom: 8 }}>📄</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>GAMMIS PDF</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>CRA-xxxxx-R remittance<br/>Parsed by Claude AI</div>
                  </div>
                  <div onClick={() => setEraStep('pickfile')}
                    style={{ border: '1.5px solid var(--border)', borderRadius: 10, padding: '20px 16px', textAlign: 'center', cursor: 'pointer', background: 'var(--bg2)' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>
                    <div style={{ fontSize: 28, marginBottom: 8 }}>📃</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>835 EDI File</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>X12 electronic ERA<br/>Downloaded from the GAMMIS portal</div>
                  </div>
                </div>
                <input ref={raFileRef} type="file" accept=".pdf" style={{ display: 'none' }} onChange={handleRAPdfFile} />
                {raError && <div style={{ marginTop: 12, padding: '10px 14px', background: 'var(--danger-bg)', borderRadius: 6, fontSize: 12, color: 'var(--danger)' }}>{raError}</div>}
              </>
            )}

            {/* ── PDF uploading / parsing ── */}
            {raStep === 'uploading' && (
              <div style={{ textAlign: 'center', padding: '32px 0' }}>
                <div className="loading-spinner" style={{ margin: '0 auto 16px' }} />
                <div style={{ fontSize: 13, color: 'var(--text2)' }}>Parsing PDF with Claude AI…</div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 8 }}>
                  Large remittance files take 1–2 minutes. Please wait.
                  {raElapsed > 0 && <span style={{ marginLeft: 6, fontVariantNumeric: 'tabular-nums' }}>({raElapsed}s)</span>}
                </div>
              </div>
            )}

            {/* ── PDF review ── */}
            {raStep === 'review' && raParsed && (
              <>
                <div style={{ display: 'flex', gap: 16, marginBottom: 14, padding: '10px 14px', background: 'var(--success-bg)', borderRadius: 8, border: '0.5px solid var(--success-border)', flexWrap: 'wrap' }}>
                  {[
                    { label: 'RA Number',     value: raParsed.ra_number || '—' },
                    { label: 'Check Date',    value: raParsed.check_date || '—' },
                    { label: 'Total Payment', value: fmt$2(raParsed.total_paid) },
                    { label: 'Claims found',  value: raParsed.claims.length },
                  ].map(s => (
                    <div key={s.label}>
                      <div style={{ fontSize: 9, color: 'var(--success)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>{s.label}</div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--success)' }}>{s.value}</div>
                    </div>
                  ))}
                </div>
                <div style={{ maxHeight: 340, overflowY: 'auto', border: '0.5px solid var(--border)', borderRadius: 8 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead style={{ position: 'sticky', top: 0, background: 'var(--bg2)' }}>
                      <tr>
                        {['Member','Medicaid ID','DOS','Code','Billed','Allowed','Paid','EOBS','Status'].map(h => (
                          <th key={h} style={{ padding: '7px 10px', textAlign: 'left', fontSize: 10, color: 'var(--text3)', fontWeight: 500, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {raParsed.claims.map((c, i) => {
                        const isPaid = c.status === 'paid'; const isDenied = c.status === 'denied';
                        return (
                          <tr key={i} style={{ borderTop: '0.5px solid var(--border)', background: isDenied ? 'var(--danger-bg)' : 'transparent' }}>
                            <td style={{ padding: '6px 10px', fontWeight: 500, whiteSpace: 'nowrap' }}>{c.client_name || '—'}</td>
                            <td style={{ padding: '6px 10px', fontFamily: 'monospace', fontSize: 11, color: 'var(--text3)' }}>{c.medicaid_id || '—'}</td>
                            <td style={{ padding: '6px 10px', color: 'var(--text2)', whiteSpace: 'nowrap' }}>{c.service_date || '—'}</td>
                            <td style={{ padding: '6px 10px', fontFamily: 'monospace', fontSize: 11 }}>{c.service_code || '—'}</td>
                            <td style={{ padding: '6px 10px' }}>{fmt$2(c.billed)}</td>
                            <td style={{ padding: '6px 10px', color: 'var(--text2)' }}>{fmt$2(c.allowed)}</td>
                            <td style={{ padding: '6px 10px', fontWeight: 500, color: isPaid ? 'var(--success)' : isDenied ? 'var(--danger)' : 'var(--text)' }}>{fmt$2(c.paid)}</td>
                            <td style={{ padding: '6px 10px', fontSize: 10, color: 'var(--danger)' }}>{c.eobs_codes || '—'}</td>
                            <td style={{ padding: '6px 10px' }}>
                              <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 7px', borderRadius: 20,
                                background: isPaid ? 'var(--success-bg)' : isDenied ? 'var(--danger-bg)' : 'var(--bg2)',
                                color: isPaid ? 'var(--success)' : isDenied ? 'var(--danger)' : 'var(--text3)' }}>
                                {c.status}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {raError && <div style={{ marginTop: 10, padding: '10px 14px', background: 'var(--danger-bg)', borderRadius: 6, fontSize: 12, color: 'var(--danger)' }}>{raError}</div>}
              </>
            )}

            {/* ── PDF posting ── */}
            {raStep === 'posting' && (
              <div style={{ textAlign: 'center', padding: '32px 0' }}>
                <div className="loading-spinner" style={{ margin: '0 auto 16px' }} />
                <div style={{ fontSize: 13, color: 'var(--text2)' }}>Matching and posting payments…</div>
              </div>
            )}

            {/* ── PDF done ── */}
            {raStep === 'done' && raResults && (
              <div style={{ textAlign: 'center', padding: '8px 0' }}>
                <div style={{ fontSize: 32, marginBottom: 10 }}>✅</div>
                <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--success)', marginBottom: 12 }}>Payments Posted</div>
                <div style={{ display: 'flex', gap: 20, justifyContent: 'center', marginBottom: 16 }}>
                  <div>
                    <div style={{ fontSize: 24, fontWeight: 600, color: 'var(--success)' }}>{raResults.matched.length}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>Matched & posted</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 24, fontWeight: 600, color: raResults.unmatched.length > 0 ? 'var(--warn)' : 'var(--text3)' }}>{raResults.unmatched.length}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>Unmatched</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 24, fontWeight: 600, color: 'var(--success)' }}>
                      {fmt$2(raResults.matched.reduce((s, m) => s + (m.update.paid_amount || 0), 0))}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>Total posted</div>
                  </div>
                </div>
                <RemittanceOutcome results={raResults} />
                <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 12 }}>Expand the run below to review updated claim lines.</div>
              </div>
            )}

            {/* ── 835 EDI file picker ── */}
            {eraStep === 'pickfile' && (
              <>
                <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
                  Download the 835 ERA file from the GAMMIS portal, then upload it here.
                </p>
                <div onClick={() => eraFileRef.current?.click()}
                  style={{ border: '1.5px dashed var(--border)', borderRadius: 10, padding: '32px 24px', textAlign: 'center', cursor: 'pointer', background: 'var(--bg2)' }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>
                  <div style={{ fontSize: 28, marginBottom: 8 }}>📃</div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 4 }}>Click to select ERA file</div>
                  <div style={{ fontSize: 11, color: 'var(--text3)' }}>.835 · .txt · .edi — any standard X12 835 file</div>
                </div>
                <input ref={eraFileRef} type="file" accept=".835,.txt,.edi,.x12" style={{ display: 'none' }} onChange={handleERAFile} />
                {eraError && <div style={{ marginTop: 12, padding: '10px 14px', background: 'var(--danger-bg)', borderRadius: 6, fontSize: 12, color: 'var(--danger)' }}>{eraError}</div>}
                <div style={{ marginTop: 16, fontSize: 12, color: 'var(--text3)' }}>
                  <strong>Matching priority:</strong> ICN number → Medicaid ID + service date + HCPC code.
                </div>
              </>
            )}

            {eraStep === 'pick' && raStep !== 'type' ? null : null}
            {eraStep === 'pickfile' ? null : null}
            {eraStep === 'review' && eraParsed && (
              <>
                <div style={{ display: 'flex', gap: 16, marginBottom: 14, padding: '10px 14px', background: 'var(--success-bg)', borderRadius: 8, border: '0.5px solid var(--success-border)' }}>
                  {[
                    { label: 'Check / ERA', value: eraParsed.checkNumber || '—' },
                    { label: 'Check Date',  value: eraParsed.checkDate || '—' },
                    { label: 'Total Payment', value: fmt$2(eraParsed.totalPayment) },
                    { label: 'Claims in ERA', value: eraParsed.claims.length },
                  ].map(s => (
                    <div key={s.label}>
                      <div style={{ fontSize: 9, color: 'var(--success)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>{s.label}</div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--success)' }}>{s.value}</div>
                    </div>
                  ))}
                </div>
                <div style={{ maxHeight: 340, overflowY: 'auto', border: '0.5px solid var(--border)', borderRadius: 8 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead style={{ position: 'sticky', top: 0, background: 'var(--bg2)' }}>
                      <tr>
                        {['Patient','Medicaid ID','Svc Code','DOS','Billed','Paid','Adj Reason','ICN','Status'].map(h => (
                          <th key={h} style={{ padding: '7px 10px', textAlign: 'left', fontSize: 10, color: 'var(--text3)', fontWeight: 500, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {eraParsed.claims.map((c, i) => {
                        // parse835 emits camelCase. This table was left on the
                        // snake_case names the in-page parser used before it moved
                        // into x835.js, so `c.service_lines[0]` threw on the first
                        // 835 anyone ever opened — which is why it took until now
                        // to notice: AHFC has never received one.
                        const svc = (c.serviceLines || [])[0];
                        const adj = [...(c.adjustments || []), ...(svc?.adjustments || [])].find(a => ['CO','OA','PR'].includes(a.group));
                        const isPaid = c.status === 'paid'; const isDenied = c.status === 'denied';
                        return (
                          <tr key={i} style={{ borderTop: '0.5px solid var(--border)', background: isDenied ? 'var(--danger-bg)' : 'transparent' }}>
                            <td style={{ padding: '6px 10px', fontWeight: 500, whiteSpace: 'nowrap' }}>{c.patientName || '—'}</td>
                            <td style={{ padding: '6px 10px', fontFamily: 'monospace', fontSize: 11, color: 'var(--text3)' }}>{c.medicaidId || '—'}</td>
                            <td style={{ padding: '6px 10px', fontFamily: 'monospace', fontSize: 11 }}>{svc?.serviceCode || '—'}{svc?.modifier ? ` ${svc.modifier}` : ''}</td>
                            <td style={{ padding: '6px 10px', color: 'var(--text2)', whiteSpace: 'nowrap' }}>{svc?.fromDate || '—'}</td>
                            <td style={{ padding: '6px 10px' }}>{fmt$2(c.billed)}</td>
                            <td style={{ padding: '6px 10px', fontWeight: 500, color: isPaid ? 'var(--success)' : isDenied ? 'var(--danger)' : 'var(--text)' }}>{fmt$2(c.paid)}</td>
                            <td style={{ padding: '6px 10px', fontSize: 10, color: 'var(--text3)' }}>{adj ? `${adj.group}-${adj.code} (${fmt$2(adj.amount)})` : '—'}</td>
                            <td style={{ padding: '6px 10px', fontFamily: 'monospace', fontSize: 10, color: 'var(--text3)' }}>{c.icn || '—'}</td>
                            <td style={{ padding: '6px 10px' }}>
                              <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 7px', borderRadius: 20,
                                background: isPaid ? 'var(--success-bg)' : isDenied ? 'var(--danger-bg)' : 'var(--bg2)',
                                color: isPaid ? 'var(--success)' : isDenied ? 'var(--danger)' : 'var(--text3)' }}>
                                {c.status}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {eraError && <div style={{ marginTop: 10, padding: '10px 14px', background: 'var(--danger-bg)', borderRadius: 6, fontSize: 12, color: 'var(--danger)' }}>{eraError}</div>}
              </>
            )}
            {eraStep === 'posting' && (
              <div style={{ textAlign: 'center', padding: '32px 0' }}>
                <div className="loading-spinner" style={{ margin: '0 auto 16px' }} />
                <div style={{ fontSize: 13, color: 'var(--text2)' }}>Matching and posting payments…</div>
              </div>
            )}
            {eraStep === 'done' && eraResults && (
              <div style={{ textAlign: 'center', padding: '8px 0' }}>
                <div style={{ fontSize: 32, marginBottom: 10 }}>✅</div>
                <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--success)', marginBottom: 12 }}>Payments Posted</div>
                <div style={{ display: 'flex', gap: 20, justifyContent: 'center', marginBottom: 16 }}>
                  <div>
                    <div style={{ fontSize: 24, fontWeight: 600, color: 'var(--success)' }}>{eraResults.matched.length}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>Matched & posted</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 24, fontWeight: 600, color: eraResults.unmatched.length > 0 ? 'var(--warn)' : 'var(--text3)' }}>{eraResults.unmatched.length}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>Unmatched</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 24, fontWeight: 600, color: 'var(--success)' }}>
                      {fmt$2(eraResults.matched.reduce((s, m) => s + (m.update.paid_amount || 0), 0))}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>Total posted</div>
                  </div>
                </div>
                <RemittanceOutcome results={eraResults} />
                <div style={{ fontSize: 12, color: 'var(--text3)' }}>Expand the run below to review updated claim lines.</div>
              </div>
            )}
          </div>
          <div className="modal-footer">
            {/* PDF flow footer */}
            {raStep === 'type' && eraStep === 'pick' && <button className="btn btn-outline" onClick={() => onClose()}>Cancel</button>}
            {raStep === 'uploading' && (
              <button className="btn btn-outline" onClick={() => { raCancelledRef.current = true; raAbortRef.current?.abort(); }}>Cancel</button>
            )}
            {raStep === 'review' && (
              <>
                <button className="btn btn-outline" onClick={() => { setRaStep('type'); setRaParsed(null); if (raFileRef.current) raFileRef.current.value = ''; }}>← Re-upload</button>
                <button className="btn btn-primary" onClick={postRAClaims}>
                  Post {raParsed?.claims.length} Payment{raParsed?.claims.length !== 1 ? 's' : ''} →
                </button>
              </>
            )}
            {raStep === 'done' && <button className="btn btn-primary" onClick={() => onClose()}>Done</button>}
            {/* 835 EDI flow footer */}
            {eraStep === 'pickfile' && (
              <>
                <button className="btn btn-outline" onClick={() => setEraStep('pick')}>← Back</button>
              </>
            )}
            {eraStep === 'review' && (
              <>
                {/* There is nothing to re-upload when the file came off the
                    SFTP server rather than somebody's desktop. */}
                {eraModal.storedId ? (
                  <button className="btn btn-outline" onClick={() => onClose()}>Cancel</button>
                ) : (
                  <button className="btn btn-outline" onClick={() => { setEraStep('pickfile'); if (eraFileRef.current) eraFileRef.current.value = ''; }}>← Re-upload</button>
                )}
                <button className="btn btn-primary" onClick={postERA}>
                  Post {eraParsed?.claims.length} Payment{eraParsed?.claims.length !== 1 ? 's' : ''} →
                </button>
              </>
            )}
            {eraStep === 'done' && <button className="btn btn-primary" onClick={() => onClose()}>Done</button>}
          </div>
        </div>
      </div>
  );
}
